import React, {useEffect, useId, useState} from 'react';

import {DatasetFullDefaultConfig, TimeConfigDefaultDefined} from "models/dataset-models";
import {timeConfigValidation} from "utils/utils";
import {DatasetDataApi} from "../../../api/DatasetDataApi";
import {addToDate, ChronoUnit, dateToReadableString, truncateDate} from "../../../utils/time-utils";
import {DatasetDataInfoResponse} from "../../../api/apiModels";
import {Button} from "primereact/button";
import {Calendar} from "primereact/calendar";
import {TitleOverrideAndDescriptionTemplate} from "../../templates/DataTableCellTemplates";
import {Paginator} from "primereact/paginator";
import {ProgressSpinner} from "primereact/progressspinner";

import './DatasetTable.scss';

const dataApi = new DatasetDataApi()

const DatasetTable = ({datasets, config, reportOptions, referenceTime}: DatasetFullDefaultConfig) => {
    const tableId = useId().replace(/[^a-zA-Z0-9]/g, '');

    const configComponent = React.useState<TimeConfigDefaultDefined>({
        alignStartTo: "HOURS" as ChronoUnit,
        startOffsetUnits: "HOURS" as ChronoUnit,
        startOffset: 0,
        lagUnits: "HOURS" as ChronoUnit,
        lagOffset: 0,
        leadUnits: "HOURS" as ChronoUnit,
        leadOffset: 0,

    });

    const identifiers = React.useRef<Array<string>>([]);

    const [refresh, setRefresh] = useState<boolean | undefined>(undefined);

    const [data, setData] = useState<Array<DatasetDataInfoResponse>>([]);

    const [datasetsArray, setDatasetsArray] = useState<Array<any>>(datasets ? datasets : []);

    const [dateFrom, setDateFrom] = useState<Date | undefined>();
    const [dateTo, setDateTo] = useState<Date | undefined>();

    const [loading, setLoading] = useState<boolean>(true);
    const [requesting, setRequesting] = useState<boolean>(false);

    const [totalSteps, setTotalSteps] = useState<Array<Date>>();

    const [currentPage, setCurrentPage] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [rows, setRows] = useState(config?.itemsPerPage || 24)

    const rowsPerPageOptions = config?.itemsPerPage ? [config.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
            return a.indexOf(x) == i;
        }) : [10, 20, 24, 30];

    useEffect(() => {
        let tempIdentifiers = datasets?.map(ob => ob.identifier);
        if (tempIdentifiers && tempIdentifiers.length > 0) {
            identifiers.current = tempIdentifiers;
        }
        truncateStarterDateFromConfig();
        setRefresh(true);
    }, []);

    useEffect(() => {
        if (!loading) {
            document.getElementsByClassName('min-table-width')[0]!!.style.minWidth = (identifiers.current.length * 100 + 100) + "px";

            if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
                const table = document.querySelector(`#${tableId}`);
                if (table) {
                    const allChildren = table.querySelectorAll('*')

                    if (reportOptions.fontSize) {

                        for (let i = 0; i < allChildren.length; i++) {
                            allChildren[i].style.fontSize = reportOptions?.fontSize;
                        }
                    }
                }
            }
        }
    }, [loading])

    useEffect(() => {
        getStepsCount();
    }, [dateFrom, dateTo]);

    useEffect(() => {
        getDataInfoRange();
    }, [totalSteps, rows]);

    useEffect(() => {
        if (currentPage !== undefined) {
            getDataInfoRange();
        }
    }, [currentPage]);

    useEffect(() => {
        if (refresh) {
            let frequency = 0;
            if (config && config.startAutoRefreshFrequency) {
                frequency = config.startAutoRefreshFrequency > 0 ? config.startAutoRefreshFrequency * 1000 : 0;
            }
            truncateStarterDateFromConfig();
            if (frequency > 0) {
                const interval = setInterval(() => {
                    truncateStarterDateFromConfig();
                }, frequency);
                return () => clearInterval(interval);
            }
        }
    }, [refresh])


    const getStepsCount = () => {
        if (dateFrom && dateTo) {
            let {from, to} = compareTwoDates(dateTo, dateFrom);
            let stepSize: number = config ? config.stepSize ? config.stepSize : 1 : 1;
            let stepUnits: ChronoUnit = config ? config.stepUnits ? config.stepUnits : ChronoUnit.HOURS : ChronoUnit.HOURS;
            let stepsArr: Array<Date> = [];

            while (from <= to) {
                stepsArr.push(from);
                from = addToDate(from, stepSize, stepUnits);
            }
            setTotalSteps([...stepsArr]);
        }
    }

    const compareTwoDates = (first: Date, second: Date) => {
        if (first > second) {
            return {from: second, to: first}
        } else {
            return {from: first, to: second}
        }
    }

    const truncateStarterDateFromConfig = () => {
        let {from, to} = getTimeRange();
        if (new Date(from).getTime() !== dateFrom?.getTime() ||
            new Date(to).getTime() !== dateTo?.getTime()) {
            setDateFrom(new Date(from));
            setDateTo(new Date(to));
        }
        if (!totalSteps) {
            getStepsCount();
        }
    }

    const getDataInfoRange = () => {
        if (!requesting) {
            if (identifiers.current.length > 0 && totalSteps ? totalSteps.length : 0 > 0) {
                let {from, to} = calculatePageDates(currentPage ? currentPage : 0);
                let fromStep = totalSteps ? totalSteps[from] : new Date();
                let toStep = totalSteps ? totalSteps[to] : new Date();
                setRequesting(true);
                dataApi.getDataInfoRange(identifiers.current,
                    fromStep.toISOString() as string,
                    toStep.toISOString() as string)
                    .then(({error, data}) => {
                        setRequesting(false);
                        if (loading) {
                            setLoading(false);
                        }

                        if (error) {
                            console.error("Error")
                            return
                        }

                        if (data) {
                            mapAndSetDatasetArray(data);
                            setData(mapData(data));
                        }
                    })
            }
        }
    }

    const mapAndSetDatasetArray = (data: Array<DatasetDataInfoResponse>) => {
        setDatasetsArray([...datasetsArray.map(ob => {
            let tempObj: any = {...ob};
            let found = data.find(a => a.identifier === ob.identifier);

            if (found && ob.identifier === found.identifier) {
                tempObj.title = found.title;
                tempObj.unit = found.unit;

                if (ob.decimalPlaces != null) {
                    tempObj.decimalPlaces = ob.decimalPlaces;
                } else if (found.decimalPlaces != null) {
                    tempObj.decimalPlaces = found.decimalPlaces;
                } else {
                    tempObj.decimalPlaces = 2;
                }

                return tempObj
            }

            return ob
        })]);
    }

    const getInfoRange = async () => {
        let {from, to} = calculatePageDates(-1);
        let fromStep = totalSteps ? totalSteps[from] : new Date();
        let toStep = totalSteps ? totalSteps[to] : new Date();
        return await dataApi.getDataInfoRange(identifiers.current,
            fromStep.toISOString() as string,
            toStep.toISOString() as string)
    }

    const exportOnClickHandler = () => {
        setRequesting(true);
        exportDataRange()
            .then(res => {
                let blob = new Blob([res as BlobPart], {type: "application/csv;charset=utf-8;"});
                let link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.style.display = "hidden";
                link.download = "datasetTable.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setRequesting(false);
            })

    }

    const exportDataRange = async () => {
        let headerLine = "Date";
        datasetsArray.forEach(ob => {
            if (ob.unit && ob.unit !== " ") {
                if (ob.overriddenTitle) {
                    headerLine = headerLine + ",\"" + ob.overriddenTitle + ", " + ob.unit + "\"";
                } else if (ob.title) {
                    headerLine = headerLine + ",\"" + ob.title + ", " + ob.unit + "\"";
                } else {
                    headerLine = headerLine + ",\"" + ob.identifier + ", " + ob.unit + "\"";
                }
            } else {
                if (ob.overriddenTitle) {
                    headerLine = headerLine + "," + ob.overriddenTitle;
                } else if (ob.title) {
                    headerLine = headerLine + "," + ob.title;
                } else {
                    headerLine = headerLine + "," + ob.identifier;
                }
            }
        })

        let bodyLine = "";
        await getInfoRange()
            .then(({error, data}) => {
                if (error) {
                    console.error("Error")
                    return
                }
                if (data) {
                    mapData(data, -1).forEach((obj: DatasetDataInfoResponse) => {
                        let newLine = "";
                        Object.entries(obj).map(ob => {
                            if (ob[0] === "date") {
                                newLine = dateBody(ob[1] as string) + newLine;
                            } else {
                                newLine += ",";
                                if (ob[1] && ob[1] !== " ") {
                                    newLine = newLine + ob[1];
                                } else {
                                    newLine = newLine + "-";
                                }
                            }
                        })
                        newLine += "\n";
                        bodyLine += newLine;
                    })
                }
            })
        return headerLine + "\n" + bodyLine;
    }

    const calculatePageDates = (pageNumber: number) => {
        let from;
        let to;
        let stepsArr = totalSteps ? totalSteps : [];
        if (pageNumber === -1) {
            from = 0;
            to = stepsArr.length - 1;
            return {from, to}
        }
        let itemsPerPage = rows;

        if (stepsArr.length > pageNumber * itemsPerPage) {
            from = pageNumber * itemsPerPage;
        } else {
            from = 0;
        }

        if (stepsArr.length > (itemsPerPage * (pageNumber + 1)) - 1) {
            to = (itemsPerPage * (pageNumber + 1)) - 1;
        } else {
            to = stepsArr.length - 1;
        }

        return {from, to}
    }

    const mapData = (data: Array<DatasetDataInfoResponse>, pages?: number) => {
        let pageNumber = pages ? pages : currentPage ? currentPage : 0;
        let dates = getAllDates(data, pageNumber);
        return buildArrayOfObjects(dates, data);
    }

    const getAllDates = (data: Array<any>, pageNumber: number): Array<any> => {
        let allTimestamps: Array<any> = [];
        let tempColumnObj: any = {};

        data.forEach(o => {
            tempColumnObj[o.identifier] = ""
        });
        if (totalSteps) {
            let {from, to} = calculatePageDates(pageNumber);
            allTimestamps = totalSteps.slice(from, to + 1).map((obj) => {
                return {
                    ...tempColumnObj,
                    date: obj.toISOString().split(".")[0] + "Z"
                }
            })
        }

        return allTimestamps;
    }

    const buildArrayOfObjects = (arr: Array<any>, data: Array<any>) => {
        let tempArr: Array<any> = [];
        let tempObj: any = {};

        arr.forEach(obj => {
            tempObj = new Object(obj);
            Object.keys(obj).forEach((ob) => {
                if (ob !== "date") {
                    let temp: Array<any> = data.find(id => id.identifier === ob).data;
                    if (temp.length > 0) {
                        if (obj.date >= temp[0].key && obj.date <= temp[temp.length - 1].key) {
                            let ans = temp.find(date => date.key === obj.date)

                            if (ans) {
                                return obj[ob] = ans.value
                            }

                        }
                    }
                }
            })
            tempArr.push(tempObj);
        })
        return tempArr;
    }

    const getTimeRange = () => {
        if (config) {
            configComponent[0] = timeConfigValidation(config);
        } else {
            configComponent[0] = timeConfigValidation({});
        }
        let tempFrom: Date, tempTo: Date;

        const startingDate = referenceTime ? new Date(referenceTime) : new Date();

        let tempDate = truncateDate(startingDate, config?.alignStartTo);
        tempFrom = tempTo = addToDate(tempDate, configComponent[0].startOffset, configComponent[0].startOffsetUnits)
        tempFrom = addToDate(tempFrom, configComponent[0].lagOffset * (-1), configComponent[0].lagUnits);
        tempTo = addToDate(tempTo, configComponent[0].leadOffset, configComponent[0].leadUnits)
        return compareTwoDates(tempFrom, tempTo);
    }

    const dateBody = (date: string) => {
        let dateDate: Date = new Date(date);
        let dateFormat = config ?
            config.dateFormat ?
                config.dateFormat
                :
                "MINUTES"
            :
            "MINUTES";
        return dateToReadableString(dateDate, dateFormat as ChronoUnit);
    }

    const ColumnHeader = () => {
        let styleObject = {
            padding: "2px 8px",
            width: "115px"
        }
        if (config) {
            if (config.dateFormat) {
                if (config.dateFormat === "YEARS") {
                    styleObject.width = "45px";
                } else if (config.dateFormat === "MONTHS") {
                    styleObject.width = "60px";
                } else if (config.dateFormat === "DAYS") {
                    styleObject.width = "80px";
                } else if (config.dateFormat === "HOURS") {
                    styleObject.width = "95px";
                } else if (config.dateFormat === "SECONDS") {
                    styleObject.width = "130px";
                }
            }
        }
        return <tr>
            <th style={styleObject}>
                Date
            </th>
            {datasetsArray?.map((col: any, i: number) => {
                return <th key={i}
                           style={{padding: "2px 8px"}}
                >{TitleOverrideAndDescriptionTemplate({
                    description: col.description,
                    title: col.title,
                    overriddenTitle: col.overriddenTitle,
                    unit: col.unit
                }, 2)}
                </th>
            })
            }
        </tr>

    }

    const columnBody = data.map((obj: any, index: number) => {
            return (<tr key={index}>
                <td style={{
                    whiteSpace: "nowrap",
                    padding: "2px 8px"
                }}>
                    {dateBody(obj.date)}
                </td>
                {Object.entries(obj).map(([key, value], index) => {
                    if (key !== "date") {
                        return <td key={index} className={"value-class"}>
                            {value || value === 0 ?
                                datasetsArray[index].decimalPlaces || datasetsArray[index].decimalPlaces === 0 ?
                                    <>{(value as number).toFixed(datasetsArray[index].decimalPlaces)}</>
                                    :
                                    <>{(value as number).toFixed(2)}</>
                                :
                                "-"
                            }</td>
                    }
                })}
            </tr>)
        }
    )

    return (
        <div className={"dataset-table-bull-window"}>
            <div className={"config-container non-printable"}>
                <div className={"date-inputs-container"}>
                    <div className={"calendar-container"}>
                        <b style={{padding: "0 0 0 5px"}}>From</b>
                        <Calendar value={dateFrom}
                                  onChange={(e) => {
                                      setRefresh(false);
                                      setDateFrom(e.value as Date);
                                  }}
                                  hourFormat="24"
                                  showTime
                                  dateFormat={"yy-mm-dd"}/>
                    </div>
                    <div className={"calendar-container"}>
                        <b style={{padding: "0 0 0 5px"}}>To</b>
                        <Calendar value={dateTo}
                                  onChange={(e) => {
                                      setRefresh(false);
                                      setDateTo(e.value as Date);
                                  }}
                                  hourFormat="24"
                                  showTime
                                  dateFormat={"yy-mm-dd"}/>
                    </div>

                </div>
                <div className={"button-container"}>
                    {!refresh &&
                        <Button icon={"pi pi-history"}
                                onClick={() => {
                                    setRefresh(true);
                                    if (currentPage !== 0) {
                                        setCurrentPage(0);
                                    }
                                    if (page !== 1) {
                                        setPage(1)
                                    }
                                }}
                                loading={requesting}
                                disabled={refresh}
                        />
                    }
                    <div className={"button-to-left"}>
                        <Button icon={"pi pi-file"}
                                onClick={() => {
                                    exportOnClickHandler();
                                }}
                                tooltip={"Export"}
                                loading={requesting}/>
                        <Button icon={"pi pi-refresh"}
                                onClick={() => {
                                    getDataInfoRange();
                                }}
                                loading={requesting}/>
                    </div>

                </div>
            </div>

            {loading ?
                <div className={"loading-screen"}>
                    <ProgressSpinner style={{overflow: "hidden"}}/>
                </div>
                :
                <div className={"table-with-paginator"}>
                    <div className={"data-table-wrapper"}>
                        <table className={"min-table-width"} id={tableId}>
                            <thead>
                            <ColumnHeader/>
                            </thead>
                            <tbody>
                            {columnBody}
                            </tbody>
                        </table>
                    </div>

                    <Paginator first={page}
                               className={"non-printable"}
                               rows={rows}
                               onPageChange={(e) => {
                                   if (!requesting) {
                                       setCurrentPage(e.page);
                                       setPage(e.first);
                                       setRows(e.rows)
                                   }
                               }}
                               totalRecords={totalSteps ? totalSteps.length : 0}
                               template={"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
                               currentPageReportTemplate={"Showing {first} to {last} of {totalRecords}"}
                               rowsPerPageOptions={rowsPerPageOptions}
                    />
                </div>
            }
        </div>
    );
}

export default DatasetTable;